import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  DataTable,
  type DataTableSubRows,
} from "../../../components/DataTable";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { InfoBox } from "../../../components/InfoBox";
import type { RestProduct } from "@KassaroAB/shared/types/product";
import type { ProductVariant } from "@KassaroAB/shared/types/productVariant";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { spacing } from "@styling/sizing";
import { ProductBulkEditBar } from "./ProductBulkEditBar";
import { useProductsTable } from "./productsTableContext";
import { renderVariantCell, rowVariants } from "./productTableColumns";
import { ProductsTableToolbar } from "./ProductsTableToolbar";

const PRODUCT_ROW_HEIGHT = 48;
const VARIANT_ROW_HEIGHT = 40;

const TableBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: ${colors.textMuted};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border-top: 1px solid ${colors.border};
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  min-width: 0;
`;

const SyncProgressTrack = styled.div`
  width: 100%;
  height: 3px;
  border-radius: 2px;
  background-color: ${colors.backgroundDark};
  overflow: hidden;
`;

const SyncProgressBar = styled.div`
  height: 100%;
  width: 40%;
  border-radius: 2px;
  background-color: ${colors.primary};
  animation: sync-progress 1.2s ease-in-out infinite;

  @keyframes sync-progress {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(350%);
    }
  }
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const ProductsTableSaveWarning = () => {
  const { t } = useTranslation();
  const { edits, stock } = useProductsTable();
  const failedCount = edits.saveResult?.failed.length ?? 0;

  if (failedCount === 0 && !stock.saveError) {
    return null;
  }

  return (
    <>
      {failedCount > 0 && (
        <InfoBox variant="warning">
          {t("productsView.bulkFailed", { count: failedCount })}
        </InfoBox>
      )}
      {stock.saveError && (
        <InfoBox variant="warning">
          {t("productsView.stockSaveFailed", { error: stock.saveError })}
        </InfoBox>
      )}
    </>
  );
};

export const ProductsTablePanel = () => {
  const { t } = useTranslation();
  const {
    canEdit,
    status,
    syncProgress,
    lastSyncedAt,
    filters,
    query,
    isOverlayReady,
    isOverlayError,
  } = useProductsTable();

  const showInitialLoading =
    (status === "syncing" && query.rows.length === 0) || !isOverlayReady;

  const statusText =
    status === "syncing" && syncProgress
      ? syncProgress.phase === "full"
        ? t("productsView.statusSyncingFull", {
            count: syncProgress.fetchedCount,
          })
        : t("productsView.statusSyncingIncremental", {
            count: syncProgress.fetchedCount,
          })
      : status === "syncing"
        ? t("productsView.statusSyncing")
        : status === "error"
          ? t("productsView.statusError")
          : status === "ready"
            ? t("productsView.statusSynced", {
                count: query.total,
                time: lastSyncedAt?.toLocaleTimeString() ?? "",
              })
            : "";

  const statusDotColor =
    status === "error"
      ? colors.danger
      : status === "ready"
        ? colors.success
        : colors.textMuted;

  return (
    <>
      <ProductsTableToolbar />

      {canEdit && filters.selectedIds.length > 0 && <ProductBulkEditBar />}

      <TableBody>
        {isOverlayError ? (
          <Centered>{t("productsView.overlayLoadFailed")}</Centered>
        ) : showInitialLoading ? (
          <Centered>
            <LoadingContainer
              message={
                syncProgress
                  ? syncProgress.phase === "full"
                    ? t("productsView.loadingFull", {
                        count: syncProgress.fetchedCount,
                      })
                    : t("productsView.loadingIncremental", {
                        count: syncProgress.fetchedCount,
                      })
                  : t("productsView.loading")
              }
            />
          </Centered>
        ) : status === "ready" && query.total === 0 ? (
          <Centered>
            {t("productsView.empty")}
            {canEdit && ` ${t("productsView.emptyHint")}`}
          </Centered>
        ) : (
          <ProductsTableDataTable />
        )}
      </TableBody>

      <Footer>
        {status === "syncing" && (
          <SyncProgressTrack>
            <SyncProgressBar />
          </SyncProgressTrack>
        )}
        <FooterRow>
          <StatusDot $color={statusDotColor} />
          {statusText}
        </FooterRow>
      </Footer>
    </>
  );
};

const ProductsTableDataTable = () => {
  const { columns, filters, query, tableMeta, edits } = useProductsTable();
  const isExpanded = tableMeta.productMeta?.isExpanded;

  const subRows = useMemo(
    (): DataTableSubRows<RestProduct, ProductVariant> => ({
      get: (product) =>
        isExpanded?.(product.id)
          ? rowVariants(product, edits.edits[product.id])
          : [],
      getId: (variant) => variant.id,
      height: VARIANT_ROW_HEIGHT,
      renderCell: (columnId, product, variant) =>
        renderVariantCell(columnId, product, variant, tableMeta.productMeta),
    }),
    [isExpanded, edits.edits, tableMeta],
  );

  return (
    <DataTable<RestProduct, ProductVariant>
      data={query.rows}
      columns={columns}
      rowCount={query.total}
      getRowId={(product) => product.id}
      sorting={filters.sorting}
      onSortingChange={filters.setSorting}
      columnVisibility={filters.columnVisibility}
      onColumnVisibilityChange={filters.setColumnVisibility}
      rowSelection={filters.rowSelection}
      onRowSelectionChange={filters.setRowSelection}
      onLoadMore={query.loadMore}
      meta={tableMeta}
      estimateRowHeight={PRODUCT_ROW_HEIGHT}
      subRows={subRows}
    />
  );
};
